import { createFileRoute, Link } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from '~/components/button';

export const Route = createFileRoute('/_authed/community/')({
  component: CommunityIndex,
});

interface Consent {
  aggregateProfile: boolean;
  peerMatching: boolean;
  rooms: boolean;
}

async function fetchJson<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(path, {
    credentials: 'include',
    headers: { 'content-type': 'application/json', ...(init.headers ?? {}) },
    ...init,
  });
  if (!res.ok) throw new Error(`${path} -> ${res.status}`);
  return (await res.json()) as T;
}

function CommunityIndex() {
  const qc = useQueryClient();
  const consent = useQuery<Consent>({
    queryKey: ['community-consent'],
    queryFn: () => fetchJson<Consent>('/api/community/consent'),
  });

  const update = useMutation({
    mutationFn: (patch: Partial<Consent>) =>
      fetchJson<Consent>('/api/community/consent', { method: 'POST', body: JSON.stringify(patch) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['community-consent'] }),
  });

  return (
    <section className="mx-auto max-w-2xl px-4 py-12">
      <h1 className="text-2xl font-semibold tracking-tight">community</h1>
      <p className="mt-2 text-sm text-muted-foreground">
        everything here is off by default. you choose what (if anything) leaves your encrypted store.
      </p>

      <section className="mt-8 rounded-[var(--radius)] border border-border p-4">
        <h2 className="text-sm font-medium">consents</h2>
        <ul className="mt-3 space-y-3 text-sm">
          <Toggle
            label="contribute to cohort statistics"
            description="your profile parameters (hedged big-five, theme tags) are aggregated with others in your persona. published numbers are noised and only released when at least 10 people contributed."
            checked={consent.data?.aggregateProfile ?? false}
            onChange={(v) => update.mutate({ aggregateProfile: v })}
          />
          <Toggle
            label="be matched with similar peers"
            description="we publish aggregate statistics for the people most like you. you see what worked for them. they never see you and you never see them."
            checked={consent.data?.peerMatching ?? false}
            onChange={(v) => update.mutate({ peerMatching: v })}
          />
          <Toggle
            label="join small rooms"
            description="up to 6 people, themed, AI-facilitated. you appear under a pseudonym to others. only you can read your own copy of the conversation."
            checked={consent.data?.rooms ?? false}
            onChange={(v) => update.mutate({ rooms: v })}
          />
        </ul>
      </section>

      <section className="mt-8 grid sm:grid-cols-2 gap-3">
        <Link
          to="/community/matches"
          className="rounded-[var(--radius)] border border-border p-4 hover:bg-muted"
        >
          <div className="font-medium">peer matches</div>
          <div className="mt-1 text-sm text-muted-foreground">
            anonymised stats from people whose pattern resembles yours.
          </div>
        </Link>
        <Link
          to="/community/rooms"
          className="rounded-[var(--radius)] border border-border p-4 hover:bg-muted"
        >
          <div className="font-medium">rooms</div>
          <div className="mt-1 text-sm text-muted-foreground">
            small, themed, ai-facilitated. max 6.
          </div>
        </Link>
      </section>

      <p className="mt-8 text-xs text-muted-foreground">
        the privacy model in plain english:{' '}
        <a href="/docs/privacy.md" className="underline">read here</a>
      </p>
    </section>
  );
}

function Toggle(props: {
  label: string;
  description: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <li>
      <label className="flex items-start gap-3 cursor-pointer">
        <input
          type="checkbox"
          checked={props.checked}
          onChange={(e) => props.onChange(e.target.checked)}
          className="mt-1"
        />
        <span>
          <span className="font-medium">{props.label}</span>
          <span className="block text-muted-foreground text-xs mt-1">{props.description}</span>
        </span>
      </label>
    </li>
  );
}
